import { ICountry } from "../models/ICountry";

/**
 * Encodes a string to a Base64URL-encoded string.
 * @param str The input string to encode.
 * @returns The Base64URL-encoded string.
 */
export function stringToBase64Url(str: string): string {
	const utf8Bytes = new TextEncoder().encode(str);
	let base64 = btoa(String.fromCharCode(...Array.from(utf8Bytes)));
	return base64
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');
}

/**
 * Decodes a Base64URL-encoded string back to a regular string.
 * @param base64url The Base64URL-encoded string.
 * @returns The decoded string.
 */
export function base64UrlToString(base64url: string): string {
	let base64 = base64url
		.replace(/-/g, '+')
		.replace(/_/g, '/');

	while (base64.length % 4 !== 0) {
		base64 += '=';
	}

	const binaryStr = atob(base64);
	const utf8Bytes = new Uint8Array(binaryStr.length);
	for (let i = 0; i < binaryStr.length; i++) {
		utf8Bytes[i] = binaryStr.charCodeAt(i);
	}

	return new TextDecoder().decode(utf8Bytes);
}

/**
 * Returns a hex color between #F6F6F6 (for 0) and #4CAF50 (for 1) based on percentage (0 to 1).
 * @param percentage A number between 0 and 1 representing the percentage.
 * @returns The corresponding hex color string.
 */
export const getHexColorByPercentage = (percentage: number) => {
	if (percentage <= 0) return "#F6F6F6";
	if (percentage >= 1) return "#4CAF50";
	const start = { r: 246, g: 246, b: 246 };   // #F6F6F6
	const end = { r: 76, g: 175, b: 80 };       // #4CAF50
	const r = Math.round(start.r + (end.r - start.r) * percentage);
	const g = Math.round(start.g + (end.g - start.g) * percentage);
	const b = Math.round(start.b + (end.b - start.b) * percentage);
	const toHex = (v: number) => v.toString(16).padStart(2, '0');
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Calculates a color for each country based on its total_score.
 * @param countries Array of country objects implementing ICountry.
 * @returns An object mapping country names to their corresponding hex color.
 */
export const calculateCountryColors = (countries: Array<{
	total_score: number,
	country_name: string,
}>) => {
	const maxScore = Math.max(...countries.map(c => c.total_score));
	const minScore = Math.max(0, Math.min(...countries.map(c => c.total_score)) - 20);

	const maxScoreNorm = maxScore - minScore;

	const cityColors = countries.reduce<{ [key: string]: string }>((acc, country) => {
		let countryName;
		switch(country.country_name) {
			case 'Czech Republic':
				countryName = 'Czechia';
				break;
			default:
				countryName = country.country_name
		}
		acc[country.country_name] = getHexColorByPercentage((country.total_score - minScore) / maxScoreNorm);
		return acc;
	}, {});
	return cityColors;
};